/* Cell Devices — first-party visit beacon v1.1 (2026-07-17; consent 2026-09-27)
   One tiny ping per page view to our OWN ops worker: pathname + external referrer hostname
   + UTM labels + a first-view-of-session flag. Privacy by design: no cookies, no identifiers,
   no fingerprinting, and the server stores no IP/user-agent (the table has no such columns).
   sessionStorage carries only non-identifying first-touch campaign labels for ONE browser
   session so the quote calculator can attribute an enquiry the customer submits themselves,
   and only after the visitor pressed Accept (v1.1, below).
   Fire-and-forget: every failure is swallowed; the page never waits on it. */
(function () {
  'use strict';
  var PING_URL = 'https://ops.celldevices.co.uk/api/t';
  var FT_KEY = 'cd_first_touch';
  var OWN_HOSTS = ['celldevices.co.uk', 'www.celldevices.co.uk', 'y3077b-1a.myshopify.com'];

  // r151 (app v776; Claude cloud CC-94; Dan, D-20260927-a (22)) — THE FIRST TOUCH NEEDS AN ACCEPT. "Necessary only", and a
  // visitor who has not answered yet, still had the first touch labels written to this browser's storage, and they rode
  // on every enquiry. Our own stored choice decides, as for every other tag (cd-consent.liquid CDConsent.granted: never a
  // platform default): without an Accept nothing is written or read, a label left from before a withdrawal is removed,
  // and an enquiry says 'unattributed'. The quote tool's half done quote is kept: it is the visitor's own work, like a
  // basket (Dan's choice). The page count stores nothing and still goes; without the stored label, a view that arrives
  // from outside the site (or with no referrer) counts as a visit's first.
  function allowed() {
    try { return !!(window.CDConsent && typeof window.CDConsent.granted === 'function' && window.CDConsent.granted()); }
    catch (e) { return false; }
  }
  function forgetFirstTouch() { try { sessionStorage.removeItem(FT_KEY); } catch (e) { /* nothing stored */ } }

  function parseUtm(search) {
    var out = { s: null, m: null, c: null };
    try {
      var q = new URLSearchParams(search || '');
      out.s = q.get('utm_source');
      out.m = q.get('utm_medium');
      out.c = q.get('utm_campaign');
    } catch (e) { /* old browsers — labels just stay null */ }
    return out;
  }

  function externalRefHost() {
    try {
      if (!document.referrer) return null;
      var host = new URL(document.referrer).hostname.toLowerCase();
      return OWN_HOSTS.indexOf(host) >= 0 ? null : host;
    } catch (e) { return null; }
  }

  // A view that did not come from one of our own pages: the stateless reading of "a visit's first view".
  function arrivedFromOutside() {
    try {
      if (!document.referrer) return true;
      return OWN_HOSTS.indexOf(new URL(document.referrer).hostname.toLowerCase()) < 0;
    } catch (e) { return true; }
  }

  // First-touch: written once per browser session, only if absent and only with an Accept. Returns true when THIS
  // pageload created it (i.e. this is the session's entry view).
  function ensureFirstTouch(utm, refHost) {
    if (!allowed()) return false;
    try {
      if (sessionStorage.getItem(FT_KEY)) return false;
      sessionStorage.setItem(FT_KEY, JSON.stringify({
        s: utm.s || null, m: utm.m || null, c: utm.c || null,
        r: refHost || null,
        l: (location.pathname || '/'),
        t: Date.now()
      }));
      return true;
    } catch (e) { return false; }
  }

  // Read the cached first-touch as a compact label for lead attribution:
  //   'ft:<source>/<medium>/<campaign>' when UTM-tagged, 'ref:<host>' when referred, 'direct' when the stored first touch
  //   has neither; 'unattributed' without an Accept (nothing is read: where they came from is not ours to know).
  //   r155 (Codex FSA-116): 'unknown' when there is no stored first touch to read (storage blocked or failed) or the read
  //   throws. It said 'direct', and the lead said "came direct" for a visit nobody could place.
  function firstTouch() {
    if (!allowed()) { forgetFirstTouch(); return 'unattributed'; }
    try {
      var raw = sessionStorage.getItem(FT_KEY);
      if (!raw) return 'unknown';
      var ft = JSON.parse(raw);
      if (ft && ft.s) return ('ft:' + ft.s + '/' + (ft.m || '-') + (ft.c ? '/' + ft.c : '')).slice(0, 120);
      if (ft && ft.r) return ('ref:' + ft.r).slice(0, 120);
      return 'direct';
    } catch (e) { return 'unknown'; }
  }

  function ping() {
    try {
      var utm = parseUtm(location.search);
      var refHost = externalRefHost();
      var entry;
      if (allowed()) entry = ensureFirstTouch(utm, refHost);
      else { forgetFirstTouch(); entry = arrivedFromOutside(); }
      var payload = JSON.stringify({
        p: (location.pathname || '/').slice(0, 120),
        r: refHost,
        us: utm.s, um: utm.m, uc: utm.c,
        e: entry ? 1 : 0
      });
      // sendBeacon posts text/plain (no CORS preflight → one request per view); fetch fallback.
      if (navigator.sendBeacon && navigator.sendBeacon(PING_URL, payload)) return;
      if (window.fetch) fetch(PING_URL, { method: 'POST', body: payload, keepalive: true }).catch(function () {});
    } catch (e) { /* never disturb the page */ }
  }

  // A choice made on this page: an Accept may start the first touch here; a decline or a withdrawal removes it at once.
  document.addEventListener('cd:consent', function (ev) {
    if (ev && ev.detail && ev.detail.granted) ensureFirstTouch(parseUtm(location.search), externalRefHost());
    else forgetFirstTouch();
  });

  window.CDBeacon = { firstTouch: firstTouch };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ping);
  } else {
    ping();
  }
}());
